import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createApiClient } from "@floline/api-client";
import { ArrowLeft } from "lucide-react";
import { API_URL } from "@/env";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface DataAccessResponse {
  data: {
    profile: {
      id: string;
      name: string;
      email: string;
      timezone: string;
      createdAt: string;
      updatedAt: string;
    } | null;
    settings: Record<string, unknown> | null;
    consents: Array<Record<string, unknown>>;
    records: Array<Record<string, unknown>>;
    events: Array<Record<string, unknown>>;
  };
  meta: {
    timestamp: string;
    recordCount: number;
    eventCount: number;
  };
}

const apiClient = createApiClient(API_URL);

function DataAccessPage() {
  const query = useQuery({
    queryKey: ["data-access"],
    queryFn: async () => apiClient.get<DataAccessResponse>("/api/data-access"),
    staleTime: 60_000,
  });

  const payload = query.data?.data;
  const bundle = payload?.data;

  return (
    <div className="container max-w-4xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">My Data</h1>
        <Button asChild variant="outline" size="sm">
          <Link to="/settings">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to settings
          </Link>
        </Button>
      </div>

      {query.isLoading && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">Loading your data…</CardContent>
        </Card>
      )}

      {query.isError && (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            Failed to load data access bundle.
          </CardContent>
        </Card>
      )}

      {bundle && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-1">
              <p>Records: {payload.meta.recordCount}</p>
              <p>Events: {payload.meta.eventCount}</p>
              <p>Consents: {bundle.consents.length}</p>
              <p>Fetched at: {new Date(payload.meta.timestamp).toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account Data (JSON)</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto rounded-md border p-3 bg-muted/30 max-h-[60vh]">
                {JSON.stringify(bundle, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/data-access")({
  component: DataAccessPage,
});
